import React from "react";

import type { GuideCell } from "./portfolioMapGuides";

type Props = {
  cell: GuideCell;
  className?: string;
  style?: React.CSSProperties;
  /** card = white popup chrome; plain = text only (quadrant overlay). */
  variant?: "card" | "plain";
  /**
   * compact = dense matrices (e.g. AH×AL);
   * matrix = slightly larger type for AH-BH guide cells only.
   */
  density?: "default" | "compact" | "matrix";
};

/** Guide content — card keeps reference sizes; plain overlays use density. */
const PortfolioMapGuideCard: React.FC<Props> = ({
  cell,
  className = "",
  style,
  variant = "plain",
  density = "default",
}) => {
  const isCard = variant === "card";
  const compact = !isCard && density === "compact";
  const matrix = !isCard && density === "matrix";

  return (
    <div
      className={
        isCard
          ? `rounded-[9px] border border-[rgba(118,74,188,0.24)] bg-white px-[13px] py-3 shadow-[0_10px_28px_rgba(31,41,55,0.18)] ${className}`
          : compact
            ? `px-2 py-1.5 ${className}`
            : matrix
              ? `px-2.5 py-2 ${className}`
              : `px-[13px] py-3 ${className}`
      }
      style={style}
    >
      <div
        className={
          isCard
            ? "mb-1.5 text-[12.5px] font-extrabold text-gray-900"
            : compact
              ? "mb-0.5 text-[10px] font-extrabold leading-tight text-gray-900"
              : matrix
                ? "mb-1 text-[11px] font-extrabold leading-tight text-gray-900"
                : "mb-1.5 text-[12.5px] font-extrabold text-gray-900"
        }
      >
        {cell.name}
      </div>
      <div
        className={
          isCard
            ? "text-[11px] leading-[1.45] text-gray-500"
            : compact
              ? "line-clamp-2 text-[8.5px] leading-[1.3] text-gray-600"
              : matrix
                ? "line-clamp-3 text-[9.5px] leading-[1.35] text-gray-600"
                : "text-[11px] leading-[1.45] text-gray-500"
        }
      >
        {cell.desc}
      </div>
      <div
        className={
          isCard
            ? "mt-2 border-t border-gray-200 pt-2 text-[11px] leading-[1.45] text-gray-900"
            : compact
              ? "mt-1 border-t border-gray-200/80 pt-1 text-[8.5px] leading-[1.3] text-gray-800"
              : matrix
                ? "mt-1.5 border-t border-gray-200/80 pt-1.5 text-[9.5px] leading-[1.35] text-gray-800"
                : "mt-2 border-t border-gray-200 pt-2 text-[11px] leading-[1.45] text-gray-900"
        }
      >
        <strong
          className={
            isCard
              ? "mb-0.5 block text-[9.5px] font-bold uppercase tracking-[0.05em] text-gray-500"
              : compact
                ? "mb-0.5 block text-[7.5px] font-bold uppercase tracking-[0.04em] text-gray-500"
                : matrix
                  ? "mb-0.5 block text-[8.5px] font-bold uppercase tracking-[0.04em] text-gray-500"
                  : "mb-0.5 block text-[9.5px] font-bold uppercase tracking-[0.05em] text-gray-500"
          }
        >
          Recommended direction
        </strong>
        <span className={compact || matrix ? "line-clamp-3" : undefined}>
          {cell.action}
        </span>
      </div>
      {cell.next && (
        <div
          className={
            isCard
              ? "mt-1.5 text-[10.5px] text-gray-400"
              : compact
                ? "mt-0.5 line-clamp-2 text-[7.5px] leading-[1.25] text-gray-500"
                : matrix
                  ? "mt-1 line-clamp-2 text-[8.5px] leading-[1.3] text-gray-500"
                  : "mt-1.5 text-[10.5px] text-gray-400"
          }
        >
          <strong>Suggested continuation:</strong> {cell.next}
        </div>
      )}
    </div>
  );
};

export default PortfolioMapGuideCard;
